import type {
  SettingsInterface,
  SettingsUnitDisplayInterface,
  SettingsPlantDisplayInterface,
  PrintPropertiesInterface,
  FontSizeInterface,
} from './types';
import {
  MetricTypes,
  ScreenCalcMethods,
  PrintFormats,
  PrintOrientations,
} from './types';
import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';

export const initialState: SettingsInterface = {
  metricType: MetricTypes.METRIC_SQUARE,
  oldMetricType: MetricTypes.METRIC_SQUARE,
  screenCalcMethod: ScreenCalcMethods.NORMAL,
  unitDisplay: {
    warningText: true,
    showLoad: true,
    showEquipmentType: true,
    showUnitNumber: false,
    showRiskLevel: true,
  },
  printProperties: {
    format: PrintFormats[0].name,
    orientation: PrintOrientations.PORTRAIT.name,
    sizes: PrintFormats[0].orientation.portrait,
    width: PrintFormats[0].orientation.portrait.px.width,
    height: PrintFormats[0].orientation.portrait.px.height,
    displayDraft: true,
  },
  plantDisplay: {
    massFlows: true,
    productPercentage: false,
    showMinorWarnings: true,
    directionalArrows: false,
    drawingSheet: false,
    reverseSizeGradiation: false,
    bottleNeck: false,
    showGrid: true,
  },
  autoSimulate: true,
  canvasBodyFontSize: {
    size: 10,
    limit: 0,
  },
  autoSave: false,
};

const settingsSlice = createSlice({
  name: 'settings',
  initialState,
  reducers: {
    setSettings(state, action: PayloadAction<Partial<SettingsInterface>>) {
      return {
        ...state,
        ...action.payload,
      };
    },
    editDisplayUnitSettings(
      state,
      action: PayloadAction<SettingsUnitDisplayInterface>,
    ) {
      state.unitDisplay = action.payload;
    },
    editDisplayPlantSettings(
      state,
      action: PayloadAction<SettingsPlantDisplayInterface>,
    ) {
      state.plantDisplay = action.payload;
    },
    editPrintProperties(
      state,
      action: PayloadAction<PrintPropertiesInterface>,
    ) {
      state.printProperties = action.payload;
    },
    setDisplayDrawingSheet(state, action: PayloadAction<boolean>) {
      state.plantDisplay.drawingSheet = action.payload;
    },
    setAutoSimulate(state, action: PayloadAction<boolean>) {
      state.autoSimulate = action.payload;
    },
    setAutoSave(state, action: PayloadAction<boolean>) {
      state.autoSave = action.payload;
    },
    setCanvasFontSize(state, action: PayloadAction<FontSizeInterface>) {
      state.canvasBodyFontSize = action.payload;
    },
    clearDesignerState() {
      return initialState;
    },
  },
  // Moved actions to other slices:
  // - setFileHasBeenEdited -> fileState slice
  // - setProjectData -> documentMetadata slice
  // - setDrawnBy -> documentMetadata slice
  // - setSimulating -> layout slice
  // - setFileStatus -> fileState slice
  // - setShowApproveFileModal -> layout slice
  // SIMULATION_RESULT extraReducer moved to documentMetadata slice
});

export const {
  clearDesignerState,
  editDisplayPlantSettings,
  editDisplayUnitSettings,
  editPrintProperties,
  setAutoSave,
  setAutoSimulate,
  setDisplayDrawingSheet,
  setSettings,
  setCanvasFontSize,
} = settingsSlice.actions;

export const settingsReducer = settingsSlice.reducer;
